// A blob URL is same-origin with the page that made it. Whatever content type the file carries
// decides what the browser does with it, so a borrower's emailed `statement.html` or an
// `image/svg+xml` opened from an object URL runs as script in the portal's own origin: it can read
// the readable `sf_csrf` cookie, reach `window.parent`, and call the API as the staff member who
// clicked. Before a file was fetched through the API client the missing bearer token (a 401) was the
// only thing in the way. So the type is decided here, from the blob's own `type` and never from a
// file extension, and nothing outside the allowlist is ever shown by the browser.
//
// Deliberately free of any import from `api.js`, which uses `openBlobInNewTab` itself.

/** Types the browser renders inline and that cannot run script in the portal's origin. */
const BINARY_INLINE = new Set(["application/pdf", "image/png", "image/jpeg", "image/gif", "image/webp"]);
/** Types shown as text; re-wrapped as plain text so they are never interpreted as markup. */
const TEXT_INLINE = new Set(["text/plain", "text/csv"]);

/** How long a tab opened on an object URL keeps it alive before it is revoked. */
export const REVOKE_AFTER_MS = 60000;

/**
 * Decides what may be done with a fetched file and returns a blob safe to put in a URL.
 *
 * `kind` is `pdf` and `image` for the allowlisted binary types (re-wrapped with exactly that type,
 * dropping parameters), `text` for `text/plain` and `text/csv` (re-wrapped as `text/plain;charset=utf-8`),
 * and `download` for everything else, re-wrapped as `application/octet-stream` so a browser that is
 * handed the URL saves it rather than rendering it.
 *
 * @param {Blob} blob the response body, with whatever type the server sent.
 * @returns {{blob: Blob, kind: "pdf"|"image"|"text"|"download"}}
 */
export const classifyBlob = (blob) => {
  const type = (blob.type || "").split(";")[0].trim().toLowerCase();
  if (type === "application/pdf") return { blob: new Blob([blob], { type }), kind: "pdf" };
  if (BINARY_INLINE.has(type)) return { blob: new Blob([blob], { type }), kind: "image" };
  if (TEXT_INLINE.has(type)) return { blob: new Blob([blob], { type: "text/plain;charset=utf-8" }), kind: "text" };
  return { blob: new Blob([blob], { type: "application/octet-stream" }), kind: "download" };
};

/**
 * Saves a blob through an anchor click.
 *
 * @param {string} url object URL of the blob.
 * @param {string} [filename] name to save it under.
 */
const downloadFromUrl = (url, filename) => {
  const a = document.createElement("a");
  a.href = url;
  a.rel = "noopener";
  if (filename) a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

/**
 * Shows a fetched file: an allowlisted type opens in a new tab, anything else is downloaded, and a
 * blocked pop-up falls back to a download. Returns `"tab"` or `"download"` for the caller and the tests.
 *
 * `window.open` is called without the `noopener` feature, because that makes it return `null` and
 * every call would look blocked; the opener link is cut by hand instead.
 *
 * The object URL is revoked after `REVOKE_AFTER_MS` so the new tab has time to load it. A tab that
 * is still loading after that, a reload of the tab later, or a download started late from a blocked
 * pop-up prompt will find the URL gone.
 *
 * @param {Blob} blob the response body, with whatever type the server sent.
 * @param {string} [filename] name used when the file is downloaded.
 * @returns {"tab"|"download"}
 */
export const openBlobInNewTab = (blob, filename) => {
  const { blob: safe, kind } = classifyBlob(blob);
  const url = URL.createObjectURL(safe);
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_AFTER_MS);
  if (kind === "download") {
    downloadFromUrl(url, filename);
    return "download";
  }
  const tab = window.open(url, "_blank");
  if (tab) {
    tab.opener = null;
    return "tab";
  }
  downloadFromUrl(url, filename);
  return "download";
};
